import { z } from "zod";
import { customerOrderedEditionSnapshotV1Schema } from "./book-fulfillment-customer.js";
import { fulfillmentProviderEnvironmentSchema, fulfillmentSchemaVersionSchema, fulfillmentSha256Schema, fulfillmentStateSchemaV1, fulfillmentPrintSourceIdSchema, } from "./fulfillment.js";
import { ulidSchema } from "./schemas.js";
export const LIFESCAPSULE_FULFILLMENT_SIMULATION_NOTICE = "Simulation only. No order is sent to Lulu, Gelato, or another printer, and no physical shipment will occur.";
export const fulfillmentRuntimeSchemaVersionSchema = z.literal("2026-09-23.2");
export const fulfillmentRuntimeSchemaVersion = "2026-09-23.2";
const timestampSchema = z.string().datetime({ offset: true });
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
const opaqueReferenceSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:#/-]*$/u);
const moneySchema = z
    .object({ currency: z.literal("USD"), amountMinor: z.number().int().nonnegative() })
    .strict();
export const physicalOrderSourceKindSchema = z.enum([
    "DIRECT_INCLUDED_COPY",
    "GIFT_INCLUDED_COPY",
    "EDITION_REORDER",
    "QVC_EVIDENCE",
    "REPLACEMENT",
    "LEGACY_LULU",
]);
export const physicalOrderSourceReferenceSchema = z
    .object({
    kind: physicalOrderSourceKindSchema,
    sourceId: opaqueReferenceSchema,
    commerceOrderId: opaqueReferenceSchema.optional(),
    entitlementId: opaqueReferenceSchema.optional(),
    giftClaimId: opaqueReferenceSchema.optional(),
    reorderId: opaqueReferenceSchema.optional(),
    qvcCaseId: opaqueReferenceSchema.optional(),
    replacedPhysicalOrderId: ulidSchema.optional(),
})
    .strict()
    .superRefine((source, context) => {
    const required = {
        DIRECT_INCLUDED_COPY: "commerceOrderId",
        GIFT_INCLUDED_COPY: "giftClaimId",
        EDITION_REORDER: "reorderId",
        QVC_EVIDENCE: "qvcCaseId",
        REPLACEMENT: "replacedPhysicalOrderId",
    };
    const field = required[source.kind];
    if (field && !source[field])
        context.addIssue({ code: "custom", path: [field], message: `${field} is required.` });
});
export const physicalOrderSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    physicalOrderId: ulidSchema,
    ownerUserId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    fulfillmentId: ulidSchema,
    source: physicalOrderSourceReferenceSchema,
    quantity: z.number().int().min(1).max(20),
    includedCopyQuantity: z.number().int().min(0).max(1),
    editionLineageId: opaqueReferenceSchema,
    orderedSnapshot: customerOrderedEditionSnapshotV1Schema.optional(),
    state: z.enum(["EVIDENCE_READY", "ACTIVE", "CANCELLED", "ARCHIVED"]),
    version: z.number().int().nonnegative(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
})
    .strict()
    .superRefine((order, context) => {
    if (order.includedCopyQuantity > order.quantity)
        context.addIssue({
            code: "custom",
            path: ["includedCopyQuantity"],
            message: "Included quantity cannot exceed total quantity.",
        });
});
export const initiatePhysicalOrderCommandSchemaV1 = z
    .object({
    previewId: fulfillmentPrintSourceIdSchema,
    expectedRevision: z.number().int().nonnegative(),
    entitlementId: ulidSchema,
    confirmedExactApprovedVersion: z.literal(true),
    replacementOfPhysicalOrderId: ulidSchema.optional(),
    address: z
        .object({
        recipientName: z.string().trim().min(1).max(50),
        line1: z.string().trim().min(1).max(35),
        line2: z.string().trim().min(1).max(35).optional(),
        city: z.string().trim().min(1).max(30),
        region: z
            .string()
            .trim()
            .length(2)
            .regex(/^[A-Z]{2}$/u),
        postalCode: z
            .string()
            .trim()
            .regex(/^\d{5}(?:-\d{4})?$/u),
        country: z.literal("US"),
        phone: z.string().trim().min(7).max(25).optional(),
    })
        .strict(),
})
    .strict();
export const submitPhysicalOrderCommandSchemaV1 = z
    .object({ confirmedSendToPrint: z.literal(true) })
    .strict();
export const physicalOrderReviewSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    reviewId: ulidSchema,
    physicalOrderId: ulidSchema,
    fulfillmentId: ulidSchema,
    ownerUserId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    sourceRevision: z.number().int().nonnegative(),
    requestDigestSha256: fulfillmentSha256Schema,
    manifestDigestSha256: fulfillmentSha256Schema,
    addressEvidence: z
        .object({
        addressId: ulidSchema,
        addressDigestSha256: fulfillmentSha256Schema,
        destinationCountry: z.literal("US"),
        confirmedAt: timestampSchema,
    })
        .strict(),
    quoteOrTestEvidence: z.discriminatedUnion("kind", [
        z
            .object({
            kind: z.literal("PROVIDER_QUOTE_PENDING_ACTIVATION"),
            providerCallsMade: z.literal(false),
            activationRequired: z.literal(true),
        })
            .strict(),
        z
            .object({
            kind: z.literal("LIFESCAPSULE_SIMULATION_TEST"),
            provider: z.literal("LIFESCAPSULE_SIMULATOR"),
            simulation: z.literal(true),
            test: z.literal(true),
            physicalShipment: z.literal(false),
            providerCallsMade: z.literal(false),
            notice: z.literal(LIFESCAPSULE_FULFILLMENT_SIMULATION_NOTICE),
        })
            .strict(),
    ]),
    state: z.literal("READY_FOR_REVIEW"),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
})
    .strict();
export const physicalOrderInitiationResponseSchemaV1 = z
    .object({
    data: z
        .object({
        physicalOrder: physicalOrderSchemaV1,
        review: physicalOrderReviewSchemaV1,
        status: z.literal("REVIEW_REQUIRED"),
    })
        .strict(),
})
    .strict();
export const fulfillmentRuntimeCapabilitiesSchema = z
    .object({
    sandboxProviderEnabled: z.boolean().default(false),
    productionProviderEnabled: z.boolean().default(false),
    catalogEvidenceRefreshEnabled: z.boolean().default(false),
    smsNotificationsEnabled: z.boolean().default(false),
    deliveryEstimateBetaEnabled: z.boolean().default(false),
    physicalOrderCreationEnabled: z.boolean().default(false),
    packageBuildEnabled: z.boolean().default(false),
    quoteEnabled: z.boolean().default(false),
    submissionEnabled: z.boolean().default(false),
    reconciliationEnabled: z.boolean().default(false),
    webhookIngestionEnabled: z.boolean().default(false),
    cancellationEnabled: z.boolean().default(false),
    customerExperienceEnabled: z.boolean().default(false),
    notificationsEnabled: z.boolean().default(false),
    adminActionsEnabled: z.boolean().default(false),
    migrationWriteEnabled: z.boolean().default(false),
})
    .strict();
export const disabledFulfillmentRuntimeCapabilities = Object.freeze({
    sandboxProviderEnabled: false,
    productionProviderEnabled: false,
    catalogEvidenceRefreshEnabled: false,
    smsNotificationsEnabled: false,
    deliveryEstimateBetaEnabled: false,
    physicalOrderCreationEnabled: false,
    packageBuildEnabled: false,
    quoteEnabled: false,
    submissionEnabled: false,
    reconciliationEnabled: false,
    webhookIngestionEnabled: false,
    cancellationEnabled: false,
    customerExperienceEnabled: false,
    notificationsEnabled: false,
    adminActionsEnabled: false,
    migrationWriteEnabled: false,
});
export const privateDeliveryAddressSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    addressId: ulidSchema,
    ownerUserId: ulidSchema,
    recipientName: z.string().trim().min(1).max(120),
    line1: z.string().trim().min(1).max(160),
    line2: z.string().trim().min(1).max(160).optional(),
    city: z.string().trim().min(1).max(100),
    region: z
        .string()
        .trim()
        .length(2)
        .regex(/^[A-Z]{2}$/u),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(?:-\d{4})?$/u),
    country: z.literal("US"),
    email: z.string().trim().email().max(254),
    phone: z.string().trim().min(7).max(30).optional(),
    confirmedAt: timestampSchema,
    version: z.number().int().nonnegative(),
})
    .strict();
export const redactedDeliveryPlanSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    deliveryPlanId: ulidSchema,
    physicalOrderId: ulidSchema,
    addressId: ulidSchema,
    providerEnvironment: fulfillmentProviderEnvironmentSchema,
    destinationCountry: z.literal("US"),
    addressConfirmed: z.boolean(),
    shippingLevel: z.literal("STANDARD"),
    includedShippingQuantity: z.number().int().min(0).max(1),
    quantity: z.number().int().min(1).max(20),
    quoteId: ulidSchema.optional(),
    quoteExpiresAt: timestampSchema.optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
})
    .strict();
export const fulfillmentQuoteSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    quoteId: ulidSchema,
    physicalOrderId: ulidSchema,
    addressId: ulidSchema,
    providerEnvironment: fulfillmentProviderEnvironmentSchema,
    productProfileId: opaqueReferenceSchema,
    productProfileVersion: opaqueReferenceSchema,
    quantity: z.number().int().min(1).max(20),
    shippingLevel: z.literal("STANDARD"),
    providerShipmentMethodReference: opaqueReferenceSchema,
    productionCost: moneySchema,
    shippingCost: moneySchema,
    taxCost: moneySchema,
    taxAssessment: z.literal("NOT_PROVIDED_BY_PROVIDER_QUOTE"),
    totalCost: moneySchema,
    vendorQuoteDigestSha256: fulfillmentSha256Schema,
    quotedAt: timestampSchema,
    expiresAt: timestampSchema,
})
    .strict()
    .superRefine((quote, context) => {
    if (Date.parse(quote.expiresAt) <= Date.parse(quote.quotedAt))
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "Quote expiry must follow quote creation.",
        });
    const sum = quote.productionCost.amountMinor + quote.shippingCost.amountMinor + quote.taxCost.amountMinor;
    if (sum !== quote.totalCost.amountMinor)
        context.addIssue({
            code: "custom",
            path: ["totalCost"],
            message: "Quote total must equal its cost components.",
        });
});
export const fulfillmentCostPolicySchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    policyId: opaqueReferenceSchema,
    maxPerOrderMinor: z.number().int().positive(),
    maxDailyMinor: z.number().int().positive(),
    maxMonthlyMinor: z.number().int().positive(),
    maxQuoteDriftMinor: z.number().int().nonnegative(),
    maxQuoteDriftBasisPoints: z.number().int().min(0).max(10_000),
    quoteTtlSeconds: z.number().int().min(60).max(900),
})
    .strict();
export const gelatoProductActivationStateSchema = z.enum([
    "DRAFT",
    "EVIDENCE_PENDING",
    "REVIEWED_INACTIVE",
    "ACTIVATION_BLOCKED",
    "ACTIVE",
]);
export const gelatoProductProfileSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    profileId: opaqueReferenceSchema,
    profileVersion: opaqueReferenceSchema,
    activationState: gelatoProductActivationStateSchema,
    activationEnvironments: z.array(fulfillmentProviderEnvironmentSchema).min(1).max(2).optional(),
    vendorProductUid: opaqueReferenceSchema.optional(),
    catalogEvidence: z
        .object({
        capturedAt: timestampSchema,
        digestSha256: fulfillmentSha256Schema,
        sourceUrl: z.string().url(),
        reviewedBy: opaqueReferenceSchema.optional(),
        reviewedAt: timestampSchema.optional(),
    })
        .strict(),
    artifactTemplateEvidence: z
        .object({
        state: z.enum(["PENDING", "REVIEWED"]),
        sourceUrl: z.string().url().optional(),
        digestSha256: fulfillmentSha256Schema.optional(),
        reviewedAt: timestampSchema.optional(),
    })
        .strict(),
    activationBlockers: z.array(safeCodeSchema).max(20),
    supportedCountries: z.array(z.literal("US")).min(1).max(1),
    validPageCounts: z.array(z.number().int().positive().max(1_000)).min(1).max(1_000),
    dimensions: z
        .object({
        unit: z.literal("POINTS"),
        interiorWidth: z.number().positive(),
        interiorHeight: z.number().positive(),
        coverWidth: z.number().positive(),
        coverHeight: z.number().positive(),
        tolerancePoints: z.number().nonnegative().max(2),
        evidenceDigestSha256: fulfillmentSha256Schema,
    })
        .strict(),
    packageRecipe: z
        .object({
        recipeVersion: opaqueReferenceSchema,
        /** Exact pageCount sent to Gelato quote, price, dimension, and order APIs. */
        providerPageCount: z.number().int().positive().max(1_000).optional(),
        /** Printable pages emitted by the book-creation pipeline's interior PDF. */
        interiorPageCount: z.number().int().positive().max(1_000).optional(),
        /** Pages in the single PDF uploaded to Gelato after package assembly. */
        assembledFilePageCount: z.number().int().positive().max(1_000).optional(),
        /** pagesCount returned by Gelato's cover-dimensions response. */
        coverDimensionsReportedPageCount: z.number().int().positive().max(1_000).optional(),
        order: z
            .array(z.enum(["COVER", "FRONT_ENDPAPER", "INTERIOR", "BACK_ENDPAPER"]))
            .min(2)
            .max(4),
        frontEndpaper: z.enum(["REQUIRED", "FORBIDDEN"]),
        backEndpaper: z.enum(["REQUIRED", "FORBIDDEN"]),
    })
        .strict(),
    validation: z
        .object({
        requireEmbeddedFonts: z.boolean(),
        minimumImageDpi: z.number().int().min(72).max(1_200),
        pdfConformance: z.literal("NOT_EXTERNALLY_VALIDATED"),
    })
        .strict(),
    costPolicy: fulfillmentCostPolicySchemaV1,
})
    .strict()
    .superRefine((profile, context) => {
    if (profile.activationState === "ACTIVE" && !profile.vendorProductUid)
        context.addIssue({
            code: "custom",
            path: ["vendorProductUid"],
            message: "An active product requires a reviewed vendor product reference.",
        });
    if (profile.activationState === "ACTIVE" && !profile.catalogEvidence.reviewedAt)
        context.addIssue({
            code: "custom",
            path: ["catalogEvidence", "reviewedAt"],
            message: "An active product requires completed catalog review evidence.",
        });
    if (profile.activationState === "ACTIVE" &&
        (profile.artifactTemplateEvidence.state !== "REVIEWED" ||
            !profile.artifactTemplateEvidence.digestSha256 ||
            !profile.artifactTemplateEvidence.reviewedAt))
        context.addIssue({
            code: "custom",
            path: ["artifactTemplateEvidence"],
            message: "An active product requires reviewed exact-template evidence.",
        });
    if (profile.activationState === "ACTIVE" && profile.activationBlockers.length)
        context.addIssue({
            code: "custom",
            path: ["activationBlockers"],
            message: "An active product cannot retain activation blockers.",
        });
    if (profile.activationState === "ACTIVE" &&
        (!profile.activationEnvironments || !profile.activationEnvironments.length))
        context.addIssue({
            code: "custom",
            path: ["activationEnvironments"],
            message: "An active product must declare its allowed provider environments.",
        });
    const recipeCounts = [
        profile.packageRecipe.providerPageCount,
        profile.packageRecipe.interiorPageCount,
        profile.packageRecipe.assembledFilePageCount,
        profile.packageRecipe.coverDimensionsReportedPageCount,
    ];
    if (recipeCounts.some((value) => value !== undefined) &&
        recipeCounts.some((value) => value === undefined))
        context.addIssue({
            code: "custom",
            path: ["packageRecipe"],
            message: "Provider, interior, assembled-file, and cover-dimensions page counts must be reviewed together.",
        });
    if (profile.packageRecipe.providerPageCount !== undefined &&
        !profile.validPageCounts.includes(profile.packageRecipe.providerPageCount))
        context.addIssue({
            code: "custom",
            path: ["packageRecipe", "providerPageCount"],
            message: "The provider page count must be one of the product's valid page counts.",
        });
    if (profile.packageRecipe.interiorPageCount !== undefined &&
        profile.packageRecipe.assembledFilePageCount !==
            profile.packageRecipe.interiorPageCount + profile.packageRecipe.order.length - 1)
        context.addIssue({
            code: "custom",
            path: ["packageRecipe", "assembledFilePageCount"],
            message: "The assembled file count must include every non-interior recipe component.",
        });
    const isPhotoBookRecipe = profile.packageRecipe.frontEndpaper === "REQUIRED" &&
        profile.packageRecipe.backEndpaper === "REQUIRED";
    if (isPhotoBookRecipe &&
        profile.packageRecipe.providerPageCount !== undefined &&
        profile.packageRecipe.providerPageCount !== profile.packageRecipe.interiorPageCount)
        context.addIssue({
            code: "custom",
            path: ["packageRecipe", "providerPageCount"],
            message: "A photo-book provider page count must equal its printable interior page count.",
        });
    if (isPhotoBookRecipe &&
        profile.packageRecipe.providerPageCount !== undefined &&
        profile.packageRecipe.coverDimensionsReportedPageCount !==
            profile.packageRecipe.providerPageCount + 4)
        context.addIssue({
            code: "custom",
            path: ["packageRecipe", "coverDimensionsReportedPageCount"],
            message: "Gelato photo-book cover dimensions must report the provider page count plus four cover surfaces.",
        });
    if (profile.activationState === "ACTIVATION_BLOCKED" && !profile.activationBlockers.length)
        context.addIssue({
            code: "custom",
            path: ["activationBlockers"],
            message: "A blocked product must explain why activation is prohibited.",
        });
    if (new Set(profile.validPageCounts).size !== profile.validPageCounts.length)
        context.addIssue({
            code: "custom",
            path: ["validPageCounts"],
            message: "Valid page counts must be unique.",
        });
    if (new Set(profile.packageRecipe.order).size !== profile.packageRecipe.order.length)
        context.addIssue({
            code: "custom",
            path: ["packageRecipe", "order"],
            message: "Package components must occur exactly once.",
        });
});
export const gelatoProductRegistrySchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    registryVersion: opaqueReferenceSchema,
    // The launch profiles and the book variants' photobooks, one per size and length (2026-10-16).
    profiles: z.array(gelatoProductProfileSchemaV1).max(300),
    reviewedAt: timestampSchema.optional(),
})
    .strict();
export const fulfillmentCommandSchemaV1 = z.discriminatedUnion("type", [
    z
        .object({
        schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
        commandId: ulidSchema,
        type: z.literal("SUBMIT"),
        expectedQuoteId: ulidSchema.optional(),
        fulfillmentId: ulidSchema,
        expectedVersion: z.number().int().nonnegative(),
        requestedAt: timestampSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
        commandId: ulidSchema,
        type: z.literal("RECONCILE"),
        fulfillmentId: ulidSchema,
        expectedVersion: z.number().int().nonnegative(),
        reasonCode: safeCodeSchema,
        requestedAt: timestampSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
        commandId: ulidSchema,
        type: z.literal("CANCEL"),
        fulfillmentId: ulidSchema,
        expectedVersion: z.number().int().nonnegative(),
        reasonCode: safeCodeSchema,
        requestedAt: timestampSchema,
    })
        .strict(),
    z
        .object({
        schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
        commandId: ulidSchema,
        type: z.literal("ACKNOWLEDGE"),
        fulfillmentId: ulidSchema,
        expectedVersion: z.number().int().nonnegative(),
        reasonCode: safeCodeSchema,
        requestedAt: timestampSchema,
    })
        .strict(),
]);
export const fulfillmentQueueEnvelopeSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    messageId: ulidSchema,
    command: fulfillmentCommandSchemaV1,
    correlationId: opaqueReferenceSchema,
    enqueuedAt: timestampSchema,
})
    .strict();
export const fulfillmentEvidenceRecordSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    evidenceId: ulidSchema,
    fulfillmentId: ulidSchema,
    sequence: z.number().int().nonnegative(),
    type: safeCodeSchema,
    safeCode: safeCodeSchema,
    payloadDigestSha256: fulfillmentSha256Schema,
    actorType: z.enum(["SYSTEM", "CUSTOMER", "OPERATOR", "PROVIDER_HINT"]),
    occurredAt: timestampSchema,
    recordedAt: timestampSchema,
})
    .strict();
export const fulfillmentIdempotencyReceiptSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    scope: safeCodeSchema,
    keyDigestSha256: fulfillmentSha256Schema,
    requestDigestSha256: fulfillmentSha256Schema,
    resultReference: opaqueReferenceSchema,
    createdAt: timestampSchema,
    expiresAt: timestampSchema,
})
    .strict();
export const fulfillmentOutboxRecordSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    outboxId: ulidSchema,
    aggregateId: ulidSchema,
    eventType: safeCodeSchema,
    payloadDigestSha256: fulfillmentSha256Schema,
    opaquePayload: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
    state: z.enum(["PENDING", "PUBLISHED"]),
    attemptCount: z.number().int().nonnegative().max(100),
    availableAt: timestampSchema,
    publishedAt: timestampSchema.optional(),
    createdAt: timestampSchema,
})
    .strict();
export const submissionLeaseSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    ownerTokenDigestSha256: fulfillmentSha256Schema,
    acquiredAt: timestampSchema,
    expiresAt: timestampSchema,
    createAttemptedAt: timestampSchema.optional(),
})
    .strict();
export const privateTrackingRecordSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    shipmentId: opaqueReferenceSchema,
    carrier: z.string().trim().min(1).max(80).optional(),
    trackingCode: z.string().trim().min(1).max(200).optional(),
    trackingUrl: z.string().url().optional(),
    updatedAt: timestampSchema,
})
    .strict();
export const customerFulfillmentStatusSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    physicalOrderId: ulidSchema,
    bookId: ulidSchema,
    state: fulfillmentStateSchemaV1,
    orderedSnapshot: customerOrderedEditionSnapshotV1Schema.optional(),
    headline: z.string().trim().min(1).max(120),
    guidance: z.string().trim().min(1).max(300),
    deliveryAddressConfirmationAvailable: z.boolean(),
    submissionAvailable: z.boolean(),
    sandboxPackageReplacementAvailable: z.boolean().optional(),
    safeEstimate: z
        .object({ earliest: z.string().date(), latest: z.string().date() })
        .strict()
        .optional(),
    shipments: z.array(z
        .object({
        shipmentId: opaqueReferenceSchema,
        state: z.enum(["PENDING", "SHIPPED", "IN_TRANSIT", "DELIVERED", "RETURNED", "UNKNOWN"]),
        carrier: z.string().trim().min(1).max(80).optional(),
        trackingAvailable: z.boolean(),
    })
        .strict()),
    cancellation: z
        .object({ available: z.boolean(), reasonCode: safeCodeSchema.optional() })
        .strict(),
    needsAttention: z.boolean(),
    simulation: z.boolean(),
    physicalShipment: z.boolean(),
    /** A real provider sandbox request, not a Life's Capsule simulation or physical shipment. */
    printerTest: z.boolean().optional(),
    notice: z.literal(LIFESCAPSULE_FULFILLMENT_SIMULATION_NOTICE).optional(),
    updatedAt: timestampSchema,
})
    .strict();
export const gelatoWebhookHintSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    hintId: ulidSchema,
    bodyDigestSha256: fulfillmentSha256Schema,
    providerEventReferenceDigestSha256: fulfillmentSha256Schema.optional(),
    providerEnvironment: z.enum(["SANDBOX", "PRODUCTION"]).optional(),
    eventType: z
        .enum([
        "order_status_updated",
        "order_item_status_updated",
        "order_item_tracking_code_updated",
        "order_delivery_estimate_updated",
    ])
        .optional(),
    orderReferenceDigestSha256: fulfillmentSha256Schema.optional(),
    receivedAt: timestampSchema,
    expiresAt: timestampSchema,
    state: z.literal("UNTRUSTED_RECONCILIATION_HINT"),
})
    .strict();
export const fulfillmentNotificationSchemaV1 = z
    .object({
    schemaVersion: fulfillmentRuntimeSchemaVersionSchema,
    notificationId: ulidSchema,
    recipientUserId: ulidSchema,
    physicalOrderId: ulidSchema,
    type: z.enum([
        "ORDER_RECEIVED",
        "ACCEPTED",
        "PARTIALLY_SHIPPED",
        "SHIPPED",
        "DELIVERED",
        "RETURNED",
        "NEEDS_ATTENTION",
        "TRACKING_AVAILABLE",
        "DELIVERY_DELAYED",
        "ADDRESS_ACTION_REQUIRED",
        "CANCELLATION_RESULT",
    ]),
    templateVersion: opaqueReferenceSchema,
    contentFree: z.literal(true),
    createdAt: timestampSchema,
})
    .strict();
export { fulfillmentSchemaVersionSchema };
//# sourceMappingURL=fulfillment-runtime.js.map